/**
 * A measured distance or a measured room — the `measure` answer kind.
 *
 * Two shapes, because specification §3 has two:
 *   `run`  — one dimension in feet: E7c horizontal conduit, H3 condensate.
 *   `area` — length × width: H2 mini-split sizing (the office picks tonnage).
 *
 * An EMPTY box clears the dimension rather than writing 0, and that distinction
 * is load-bearing. calc.ts treats a zero-foot condensate run as "no run at all"
 * and emits no line; validation.ts treats an absent measure as unanswered and
 * raises a gap. Writing 0 for "I haven't paced it yet" would price a standard
 * condensate route off an untouched stepper — the exact bug the comment on
 * `condensateUnits` documents.
 */

import { useId, useState, type ReactNode } from 'react';


import type { Measure } from '../../core/types';

export interface MeasureFieldProps {
  label: string;
  value: Measure | undefined;
  onChange: (next: Measure) => void;
  mode?: 'run' | 'area';
  unit?: string;
  disabled?: boolean;
}

/** '' → undefined (unanswered). Anything unparseable is also unanswered. */
function parseDimension(raw: string): number | undefined {
  const trimmed = raw.trim();
  if (trimmed === '') return undefined;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function asDraft(value: number | undefined): string {
  return value === undefined ? '' : String(value);
}

export function MeasureField({
  label,
  value,
  onChange,
  mode = 'run',
  unit = 'ft',
  disabled = false,
}: MeasureFieldProps): ReactNode {
  const id = useId();
  const measure = value ?? {};

  // Drafts, so "12." and a half-typed "1" survive a keystroke. The committed
  // value is always the parsed one.
  const [feetDraft, setFeetDraft] = useState(() => asDraft(measure.feet));
  const [lengthDraft, setLengthDraft] = useState(() => asDraft(measure.lengthFt));
  const [widthDraft, setWidthDraft] = useState(() => asDraft(measure.widthFt));

  const box = (
    boxId: string,
    ariaLabel: string,
    draft: string,
    setDraft: (next: string) => void,
    commit: (parsed: number | undefined) => void,
  ): ReactNode => (
    <input
      id={boxId}
      className="field__input field__input--mono"
      type="text"
      inputMode="decimal"
      value={draft}
      disabled={disabled}
      aria-label={ariaLabel}
      placeholder="—"
      onChange={(event) => {
        setDraft(event.target.value);
        commit(parseDimension(event.target.value));
      }}
    />
  );

  return (
    <div className="field">
<div className="field__head">
              <label className="field__label" data-legibility="question" htmlFor={`${id}-a`}>
          {label}
        </label>
      </div>
      {mode === 'run' ? (
        <div className="measure">
          {box(`${id}-a`, `${label}, in ${unit}`, feetDraft, setFeetDraft, (feet) =>
            onChange({ ...measure, feet }),
          )}
          <span className="measure__unit">{unit}</span>
        </div>
      ) : (
        <div className="measure">
          {box(`${id}-a`, `${label}, length in ${unit}`, lengthDraft, setLengthDraft, (lengthFt) =>
            onChange({ ...measure, lengthFt }),
          )}
          <span className="measure__times" aria-hidden="true">
            ×
          </span>
          {box(`${id}-b`, `${label}, width in ${unit}`, widthDraft, setWidthDraft, (widthFt) =>
            onChange({ ...measure, widthFt }),
          )}
          <span className="measure__unit">{unit}</span>
        </div>
      )}
    </div>
  );
}
